import { useEffect, useRef, useCallback } from "react"
import { Timer, Stop, CircleNotch } from "@phosphor-icons/react"
import { Button } from "../../../components/ui/button"
import { ObjectName } from "../../../components/ui/object-name"
import { useActiveTimer, useStopTimer } from "../hooks/useTimer"
import { useTimerStore } from "../stores/timer-store"
import { nowSeconds } from "../../../lib/utils"

function formatElapsed(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

export function TimerIndicator() {
  const { data: activeTimer } = useActiveTimer()
  const stopMutation = useStopTimer()
  const { elapsedSeconds, setElapsedSeconds, showEntryFormAfterStop, setStoppedTimerData } = useTimerStore()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Calculate elapsed from server-side started_at
  const calcElapsed = useCallback(() => {
    if (!activeTimer?.started_at) return 0
    return Math.max(0, Math.floor(nowSeconds() - activeTimer.started_at))
  }, [activeTimer?.started_at])

  // Start/stop the interval when timer state changes
  useEffect(() => {
    if (activeTimer) {
      setElapsedSeconds(calcElapsed())
      intervalRef.current = setInterval(() => {
        setElapsedSeconds(calcElapsed())
      }, 1000)
    } else {
      setElapsedSeconds(0)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [activeTimer, calcElapsed, setElapsedSeconds])

  const handleStop = async () => {
    if (!activeTimer) return
    const durationMinutes = Math.max(1, Math.ceil(calcElapsed() / 60))
    try {
      await stopMutation.mutateAsync()
      if (showEntryFormAfterStop) {
        setStoppedTimerData({
          matter: activeTimer.matter,
          description: activeTimer.description || "",
          category: activeTimer.category || "",
          template: activeTimer.template || "",
          durationMinutes,
        })
      }
    } catch {
      // Error handled by mutation
    }
  }

  if (!activeTimer) return null

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg border bg-card px-4 py-3 shadow-lg">
      <Timer className="h-5 w-5 text-primary animate-pulse" weight="fill" />
      <div className="text-sm">
        <div className="font-medium truncate max-w-[200px]">
          <ObjectName collection="matters" id={activeTimer.matter} />
        </div>
        <div className="font-mono text-lg tabular-nums text-foreground">
          {formatElapsed(elapsedSeconds)}
        </div>
      </div>
      <Button
        variant="danger"
        size="sm"
        onClick={handleStop}
        disabled={stopMutation.isPending}
        title="Stop Timer"
      >
        {stopMutation.isPending ? (
          <CircleNotch className="h-4 w-4 animate-spin" />
        ) : (
          <Stop className="h-4 w-4" weight="fill" />
        )}
      </Button>
    </div>
  )
}
